import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { AdminHeader } from '../dashboard/AdminHeader';

interface LoginAttempt {
  id: string;
  user_id?: string | null;
  phone_number?: string | null;
  email?: string | null;
  attempt_type: string;
  success: boolean;
  ip_address?: string | null;
  location_data?: any;
  risk_score: number;
  created_at: string;
  updated_at: string;
}

interface LoginAttemptsMapProps {
  attempts: LoginAttempt[];
  onBack: () => void;
  onSelectAttempt: (attempt: LoginAttempt) => void;
  userName: string | null;
  networkStatus: "checking" | "verified" | "failed";
  onNetworkCheck: () => void;
  onNewOrder: () => void;
  onPriceCheck: () => void;
  onEmployeeClockIn: () => void;
  onEmployeeManagement: () => void;
  onViewBackend: () => void;
  onCreditReports: () => void;
  onCostMonitor: () => void;
  onMasterProducts: () => void;
  onAdminSettings: () => void;
  onArchivedOrders: () => void;
  onSignOutAdmin: () => void;
  onLoginAttempts: () => void;
}

export const LoginAttemptsMap = ({ 
  attempts, 
  onBack, 
  onSelectAttempt,
  userName,
  networkStatus,
  onNetworkCheck,
  onNewOrder,
  onPriceCheck,
  onEmployeeClockIn,
  onEmployeeManagement,
  onViewBackend,
  onCreditReports,
  onCostMonitor,
  onMasterProducts,
  onAdminSettings,
  onArchivedOrders,
  onSignOutAdmin,
  onLoginAttempts
}: LoginAttemptsMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<LoginAttempt | null>(null);

  // Filter attempts that have location data
  const attemptsWithLocation = attempts.filter(
    attempt => attempt.location_data?.latitude && attempt.location_data?.longitude
  );

  const successCount = attemptsWithLocation.filter(a => a.success).length;
  const failedCount = attemptsWithLocation.filter(a => !a.success).length;
  const highRiskCount = attemptsWithLocation.filter(a => a.risk_score > 50).length;

  useEffect(() => {
    if (!mapContainer.current || attemptsWithLocation.length === 0) return;

    // For demo purposes, we'll create a simple map visualization
    // In a real implementation, you would use Mapbox GL JS or similar
    console.log('Map would be initialized here with', attemptsWithLocation.length, 'locations');
    
    // Mock map initialization
    if (mapContainer.current) {
      mapContainer.current.innerHTML = `
        <div style="width: 100%; height: 100%; background: linear-gradient(45deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%); border-radius: 8px; position: relative; overflow: hidden;">
          <div style="position: absolute; top: 20px; left: 20px; background: white; padding: 8px 12px; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); font-size: 12px;">
            Map View - ${attemptsWithLocation.length} locations
          </div>
          <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; color: #64748b;">
            <div style="font-size: 48px; margin-bottom: 16px;">🗺️</div>
            <div style="font-size: 16px; font-weight: 600; margin-bottom: 8px;">Interactive Map</div>
            <div style="font-size: 14px;">Mapbox integration would display actual locations here</div>
            <div style="font-size: 12px; margin-top: 8px; opacity: 0.7;">Showing ${attemptsWithLocation.length} login attempts with location data</div>
          </div>
        </div>
      `;
    }
  }, [attemptsWithLocation]);

  const getRiskBadgeVariant = (score: number) => {
    if (score >= 75) return 'destructive';
    if (score >= 50) return 'default';
    if (score >= 25) return 'secondary';
    return 'outline';
  };

  const getRiskLabel = (score: number) => {
    if (score >= 75) return 'Critical';
    if (score >= 50) return 'High';
    if (score >= 25) return 'Medium';
    return 'Low';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <AdminHeader
        userName={userName}
        networkStatus={networkStatus}
        onNetworkCheck={onNetworkCheck}
        onNewOrder={onNewOrder}
        onPriceCheck={onPriceCheck}
        onEmployeeClockIn={onEmployeeClockIn}
        onEmployeeManagement={onEmployeeManagement}
        onViewBackend={onViewBackend}
        onScannerTest={() => {}}
        onSwitchToStaff={onSignOutAdmin}
        onSuppliersManagement={() => {}}
        onCreditReports={onCreditReports}
        onCostMonitor={onCostMonitor}
        onMasterProducts={onMasterProducts}
        onAdminSettings={onAdminSettings}
        onArchivedOrders={onArchivedOrders}
        onLoginAttempts={onLoginAttempts}
      />
      
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login Attempts
            </Button>
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Login Attempts Map</h2>
              <p className="text-muted-foreground">
                Geographic distribution of login attempts
              </p>
            </div>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">With Location</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{attemptsWithLocation.length}</div>
            <p className="text-xs text-muted-foreground">
              of {attempts.length} total attempts
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{successCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{failedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Risk</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{highRiskCount}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Map */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>Geographic Distribution</span>
              </CardTitle>
              <CardDescription>
                Login attempts plotted by location data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div ref={mapContainer} className="h-96 w-full" />
            </CardContent>
          </Card>
        </div>

        {/* Location List */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Recent Locations</CardTitle>
              <CardDescription>
                Click to view attempt details
              </CardDescription>
            </CardHeader>
            <CardContent>
              {attemptsWithLocation.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No location data available for current attempts.
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {attemptsWithLocation.slice(0, 10).map((attempt) => (
                    <div
                      key={attempt.id}
                      className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                      onClick={() => onSelectAttempt(attempt)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {attempt.success ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <Badge variant={getRiskBadgeVariant(attempt.risk_score)} className="text-xs">
                            {getRiskLabel(attempt.risk_score)}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(attempt.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="text-sm font-medium">
                        {attempt.phone_number || attempt.email || 'Unknown user'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {attempt.location_data?.latitude?.toFixed(4)}, {attempt.location_data?.longitude?.toFixed(4)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {attempt.ip_address} • {attempt.attempt_type.toUpperCase()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Note about Mapbox integration */}
      <Card>
        <CardHeader>
          <CardTitle>Map Integration</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            This map view would integrate with Mapbox GL JS to display interactive maps with login attempt locations, 
            clustering, and detailed popup information. Each marker would show the attempt status, risk level, and 
            allow drilling down into specific attempt details.
          </p>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};